import EditIcon from '@mui/icons-material/Edit';
import { Button, Stack } from '@mui/material';
import Grid from '@mui/material/Grid';
import Skeleton from '@mui/material/Skeleton';
import classNames from 'classnames';
import { autobind } from 'core-decorators';
import PropTypes from 'prop-types';
import { Component, Fragment, useMemo } from 'react';
import { connect, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import Popup from '/@/components/popup';
import PrivacyBar from '/@/components/privacy-bar';
import { BrandValues, Closing, Cover, LegalText } from '/@/components/product-datasheet';
import ProductPreview from '/@/components/product-datasheet/preview';
import ProductDatasheetShare from '/@/components/product-datasheet-share';
import Title from '/@/components/title';
import settings from '/@/core/settings';
import { useCatalogProductsById } from '/@/hooks/catalog';
import {
    useDensitiesForProducts,
    useEnvironmentRangesFromApi,
    useTablesConstraintsFromAPI,
} from '/@/hooks/product-datasheet';
import { _ } from '/@/locale';
import { setCompletePreview, setZone } from '/@/store/product-datasheet/actions';
import { useProductDatasheetConfig } from '/@/store/product-datasheet/apiProviders';
import {
    getCompletePreview,
    getProductDatasheet,
    getProductDatasheetBrand,
    getSelectedFeaturesIds,
    getSelectedMetricsIds,
} from '/@/store/product-datasheet/selectors';
import { getSessionCountry } from '/@/store/session/selectors';
import { isMobile } from '/@/utils';
import { reverse } from '/@/utils/router';
import Select from '/@/v3/components/forms/select';
import styles from './styles.module.css';

function HookWrapper(Component) {
    return function WrappedComponent(props) {
        const productDatasheet = useSelector(getProductDatasheet);

        const productDatasheetConfigsQuery = useProductDatasheetConfig({ page: 1, pageSize: 10000 });
        const productDatasheetConfigs = productDatasheetConfigsQuery.data?.results || [];
        const brand = useSelector((state) =>
            getProductDatasheetBrand(state, productDatasheet, productDatasheetConfigs)
        );

        const catalogProductsQuery = useCatalogProductsById({
            ids: productDatasheet.selectedIds,
            crop: productDatasheet.type,
        });

        const { data: environmentRanges, isValidating: environmentRangesLoading } = useEnvironmentRangesFromApi({
            ids: catalogProductsQuery?.data?.map((p) => {
                return p.id;
            }),
            crop: productDatasheet.type,
        });

        const { data: densities, isLoading: densityLoading } = useDensitiesForProducts({
            ids: productDatasheet.selectedIds,
            crop: productDatasheet.type,
        });
        const { data: densityTableConstraints, isLoading: densityTableConstraintsLoading } =
            useTablesConstraintsFromAPI({
                ids: productDatasheet.selectedIds,
                crop: productDatasheet.type,
            });

        const data = useMemo(() => {
            if (!catalogProductsQuery?.data) return {};
            return Object.values(catalogProductsQuery?.data).map((profile) => {
                return {
                    ...profile,
                    environmentRanges: environmentRanges?.[profile.id],
                    densityTableConstraints: densityTableConstraints?.[profile.id],
                    densities: densities?.[profile.id],
                };
            });
        }, [catalogProductsQuery, environmentRanges, densityTableConstraints, densities]);

        return (
            <Component
                {...props}
                previewProfiles={data}
                brand={brand}
                productDatasheetConfigs={productDatasheetConfigs}
                loading={
                    catalogProductsQuery.isValidating ||
                    environmentRangesLoading ||
                    environmentRangesLoading ||
                    densityLoading ||
                    densityTableConstraintsLoading
                }
            />
        );
    };
}

@connect(
    (state, props) => {
        const productDatasheet = getProductDatasheet(state);
        return {
            productDatasheet,
            country: getSessionCountry(state),
            featuresIds: getSelectedFeaturesIds(state),
            metricsIds: getSelectedMetricsIds(state),
            completePreview: getCompletePreview(state),
        };
    },
    (dispatch) => bindActionCreators({ setZone, setCompletePreview }, dispatch)
)
@autobind
class ProductDatasheetPreview extends Component {
    static contextTypes = {
        history: PropTypes.object,
    };

    componentWillUnmount() {
        this.props.setZone(undefined);
        this.props.setCompletePreview([]);
    }

    hasProductData() {
        // Only checks if there is any selected product
        return this.props.productDatasheet.selectedIds.length > 0;
    }

    handleEditClick() {
        this.context.history(
            reverse('productDatasheet.ProductDatasheetProductsConfiguration', {
                params: {
                    type: this.props.productDatasheet.type,
                    slug: this.props.productDatasheet.slug,
                },
            })
        );
    }

    handleNavigateClick(id) {
        document.getElementById(id).scrollIntoView({
            behavior: 'smooth',
        });
    }

    handleGoToConfigurations() {
        this.context.history(reverse('productDatasheet.ProductDatasheetBrandSelection'));
    }

    renderBrandValues() {
        if (!this.props.productDatasheet.extrasIds.includes('brandValues')) return null;
        return (
            <div className={styles.container}>
                <BrandValues productDatasheet={this.props.productDatasheet.config} />
            </div>
        );
    }

    renderHeader() {
        const { brand, country } = this.props;
        const profiles = this.props.previewProfiles.map((profile) => ({
            value: profile.id,
            label: _(profile.catalogName),
        }));
        return (
            <div className={styles.header}>
                <PrivacyBar className={styles.privacyBar} />
                <div className={classNames(styles.editHeader, styles[`theme-${brand.theme}-${brand.type}`])}>
                    <Stack direction='row' spacing={2}>
                        <Button
                            variant='outlined'
                            startIcon={<EditIcon fontSize='small' />}
                            onClick={this.handleEditClick}
                        >
                            {isMobile() ? null : _('Editar')}
                        </Button>
                        <Select
                            placeholder={_('Navegar resultado')}
                            options={profiles}
                            onChange={this.handleNavigateClick}
                            color={settings.app.theme}
                        />
                        <ProductDatasheetShare
                            selectedProfiles={this.props.completePreview}
                            productDatasheet={this.props.productDatasheet}
                            country={country.name}
                            productDatasheetConfigs={this.props.productDatasheetConfigs}
                        />
                    </Stack>
                </div>
            </div>
        );
    }

    renderPreview() {
        return (
            <div className={styles.preview}>
                {this.props.previewProfiles.map((profile) => (
                    <div id={profile.id} key={profile.id}>
                        <ProductPreview
                            className={styles.container}
                            profile={profile}
                            productDatasheet={this.props.productDatasheet}
                            brand={this.props.brand}
                            country={this.props.country.code}
                        />
                    </div>
                ))}
            </div>
        );
    }

    renderLegalText() {
        if (!this.props.productDatasheet.extrasIds.includes('legalText')) return null;
        return (
            <div className={styles.container}>
                <LegalText config={this.props.productDatasheet.config} />
            </div>
        );
    }

    renderOpening() {
        if (!this.props.productDatasheet.extrasIds.includes('openingImage')) return null;
        return <Cover type={this.props.productDatasheet.type} brand={this.props.brand} className={styles.cover} />;
    }

    renderClosing() {
        if (!this.props.productDatasheet.extrasIds.includes('closingImage')) return null;
        return <Closing type={this.props.productDatasheet.type} brand={this.props.brand} />;
    }

    render() {
        return (
            <div className={styles.root}>
                {this.props.loading ? (
                    <Grid container flexDirection='column'>
                        {Array.from(new Array(4)).map((_, index) => (
                            <Grid item key={index}>
                                <Skeleton variant='rectangular' sx={{ mb: 2, mx: 4 }} height={500} />
                            </Grid>
                        ))}
                    </Grid>
                ) : (
                    <Fragment>
                        {this.hasProductData() ? (
                            <Fragment>
                                {this.renderHeader()}
                                {this.renderOpening()}
                                {this.renderPreview()}
                                {this.renderBrandValues()}
                                {this.renderLegalText()}
                                {this.renderClosing()}
                            </Fragment>
                        ) : (
                            <Popup visible>
                                <div className={styles.popupText}>
                                    <Title>{_('Ficha técnica de producto')}</Title>
                                    <div>
                                        {_(
                                            'Debe configurar la ficha técnica del producto para acceder a la vista previa'
                                        )}
                                    </div>
                                </div>
                                <div className={styles.button}>
                                    <Button variant='contained' fullWidth onClick={this.handleGoToConfigurations}>
                                        {_('Configurar ficha técnica de producto')}
                                    </Button>
                                </div>
                            </Popup>
                        )}
                    </Fragment>
                )}
            </div>
        );
    }
}

export default HookWrapper(ProductDatasheetPreview);
